import React from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import clsx from 'clsx';

import SetTypeIcon from '../../../../../components/reusable/set-type-icon/SetTypeIcon';
import TrainingSetItemStatusIcon from '../../../../../components/reusable/training-set-item-status-status/TrainingSetItemStatusIcon';
import {
  ExerciseOptionType,
  TrainingSetItemStatus,
  sideColumnWidth,
  typeColumnWidth,
} from '../../../../../shared/constants';
import { ITrainingSet } from '../../../../../types/interfaces/training-set';
import { ITrainingSetItem } from '../../../../../types/interfaces/training-set-item';
import { ITrainingSetItemOption } from '../../../../../types/interfaces/training-set-item-option';
import { getTimeValueFromOptionValue } from '../../../../../utils/datetime/date-time-utils';
import { getColumnOptions } from '../../../../../utils/ui/column-size-utils';
import { useStyles } from './FinishedTrainingSetItem.styles';

interface ITrainingSetItemProps {
  trainingSetItem: ITrainingSetItem;
  trainingSet: ITrainingSet;
  trainingId: string;
  trainingSetId: string;
  columnWidth: number;
}

export const statusColumnWidth = 40;
export default function FinishedTrainingSetItem(props: ITrainingSetItemProps) {
  const { trainingSetItem, trainingSet, columnWidth } = props;
  const theme = useTheme();

  const { classes } = useStyles(theme);
  const { t } = useTranslation();

  function renderOptionField(
    setOption: ITrainingSetItemOption,
    _index: number,
    valueFieldName: 'value' | 'value2' = 'value',
  ) {
    const option = trainingSet.exercise?.options?.find(
      (e) => e.id === setOption.optionId,
    );

    const fallbackValue =
      valueFieldName === 'value2'
        ? setOption.programValue2
        : setOption.programValue;
    const rawValue = setOption[valueFieldName] ?? fallbackValue;
    const displayValue =
      option?.type === ExerciseOptionType.time && rawValue
        ? getTimeValueFromOptionValue(rawValue, t)
        : rawValue;

    return (
      <Box
        {...getColumnOptions(columnWidth)}
        key={option?.nameKey}
        className={classes.optionFieldContainer}>
        {displayValue ? (
          <Box display="flex" alignItems="flex-end">
            <Typography variant="text-md" color="text.secondary">
              {displayValue}
            </Typography>
            {option?.unit && option?.type !== ExerciseOptionType.time ? (
              <Typography
                fontSize="10px"
                marginBottom="-4px"
                color="text.secondary">
                {option.unit.toLowerCase()}
              </Typography>
            ) : null}
          </Box>
        ) : (
          <Typography variant="text-md">-</Typography>
        )}
      </Box>
    );
  }

  function getStatusClass(): string {
    if (trainingSetItem.status === TrainingSetItemStatus.started)
      return TrainingSetItemStatus.started;

    return `${trainingSetItem.status}${trainingSetItem.orderNumber % 2 === 1 ? 'OddLine' : 'EvenLine'}`;
  }

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      className={clsx(
        classes.trainingSetItemWrapper,
        classes[getStatusClass()],
      )}>
      <Box {...getColumnOptions(typeColumnWidth)}>
        <SetTypeIcon type={trainingSetItem.type} />
      </Box>

      {!!trainingSetItem?.trainingSetItemOptions?.length && (
        <Box
          display="flex"
          alignItems="center"
          flexDirection="column"
          width="100%">
          <Box display="flex" alignItems="center">
            {trainingSet?.exercise?.splitBySide && (
              <Box {...getColumnOptions(sideColumnWidth)}>
                <Typography variant={'text-md-sb'}>
                  {t('trainings.rightShort')}
                </Typography>
              </Box>
            )}
            {trainingSetItem.trainingSetItemOptions?.map((setOption, index) =>
              renderOptionField(setOption, index),
            )}
          </Box>
          {trainingSet?.exercise?.splitBySide && (
            <Box display="flex" alignItems="center">
              <Box {...getColumnOptions(sideColumnWidth)}>
                <Typography variant={'text-md-sb'}>
                  {t('trainings.leftShort')}
                </Typography>
              </Box>

              {trainingSetItem.trainingSetItemOptions?.map((setOption, index) =>
                renderOptionField(setOption, index, 'value2'),
              )}
            </Box>
          )}
        </Box>
      )}
      <Box
        {...getColumnOptions(statusColumnWidth)}
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap={0.125}>
        <TrainingSetItemStatusIcon
          status={trainingSetItem.status || TrainingSetItemStatus.notStarted}
        />
      </Box>
    </Box>
  );
}
